/**
 * Message-window pagination and buffer bounding for the active channel:
 * older/newer paging, jump-to-latest, fetch-around, and socket-growth trimming.
 */

import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { PublicSpaceMessage } from '@adieuu/shared';
import { MAX_SPACE_LOADED_MESSAGES, trimSpaceMessages } from './spaceScrollUtils';
import type { SpaceChannelMessagesState } from './types';

type FetchChannelMessages = (
  spaceId: string,
  channelId: string,
  cursor?: string,
  options?: { mergeLatest?: boolean; direction?: 'older' | 'newer' },
) => Promise<void> | void;

type FetchMessagesAroundInternal = (
  spaceId: string,
  channelId: string,
  messageId: string,
  options?: { before?: number; after?: number },
) => Promise<PublicSpaceMessage[] | null>;

export interface SpaceMessageWindowParams {
  activeSpaceIdRef: MutableRefObject<string | null>;
  activeChannelIdRef: MutableRefObject<string | null>;
  messagesByChannel: Record<string, SpaceChannelMessagesState>;
  fetchChannelMessages: FetchChannelMessages;
  fetchMessagesAroundInternal: FetchMessagesAroundInternal;
  setMessagesByChannel: Dispatch<SetStateAction<Record<string, SpaceChannelMessagesState>>>;
}

export function useSpaceMessageWindow(params: SpaceMessageWindowParams) {
  const {
    activeSpaceIdRef,
    activeChannelIdRef,
    messagesByChannel,
    fetchChannelMessages,
    fetchMessagesAroundInternal,
    setMessagesByChannel,
  } = params;

  const loadOlderMessages = useCallback(async () => {
    const spaceId = activeSpaceIdRef.current;
    const channelId = activeChannelIdRef.current;
    if (!spaceId || !channelId) return;
    const state = messagesByChannel[channelId];
    if (!state?.olderCursor || state.loading) return;
    await fetchChannelMessages(spaceId, channelId, state.olderCursor, { direction: 'older' });
  }, [messagesByChannel, fetchChannelMessages, activeSpaceIdRef, activeChannelIdRef]);

  const loadNewerMessages = useCallback(async () => {
    const spaceId = activeSpaceIdRef.current;
    const channelId = activeChannelIdRef.current;
    if (!spaceId || !channelId) return;
    const state = messagesByChannel[channelId];
    if (!state?.hasNewerPages || state.loading) return;
    const head = state.messages[0];
    if (!head) return;
    await fetchChannelMessages(spaceId, channelId, head.id, { direction: 'newer' });
  }, [messagesByChannel, fetchChannelMessages, activeSpaceIdRef, activeChannelIdRef]);

  const jumpToLatestMessages = useCallback(
    async (channelId: string) => {
      const spaceId = activeSpaceIdRef.current;
      if (!spaceId || !channelId) return;
      // Wipe the window synchronously so the refetch takes the initial-load
      // (replace) path and lands on the live tip, rather than merging the latest
      // page onto a detached history window.
      setMessagesByChannel((prev) => ({
        ...prev,
        [channelId]: {
          messages: [],
          olderCursor: null,
          hasNewerPages: false,
          loading: true,
          loadingOverlay: false,
        },
      }));
      await fetchChannelMessages(spaceId, channelId);
    },
    [fetchChannelMessages, setMessagesByChannel, activeSpaceIdRef],
  );

  const fetchMessagesAround = useCallback(
    (messageId: string, options?: { before?: number; after?: number }) => {
      const spaceId = activeSpaceIdRef.current;
      const channelId = activeChannelIdRef.current;
      if (!spaceId || !channelId) return Promise.resolve(null);
      return fetchMessagesAroundInternal(spaceId, channelId, messageId, options);
    },
    [fetchMessagesAroundInternal, activeSpaceIdRef, activeChannelIdRef],
  );

  // Bounded buffer, trimmed in whichever direction the user is scrolling away
  // from (messages are newest-first: index 0 = newest, last = oldest).
  //
  // Fetch-driven growth is already hard-capped at merge time in
  // useSpaceDataFetching; this is the safety net for socket-driven growth (live
  // messages are prepended without a fetch):
  //  - At the live tail: keep the newest window and advance the older cursor to
  //    the new oldest so older pagination continues; head stays latest.
  //  - Scrolled up (reading history): keep the oldest window, evicting the
  //    newest overflow, and flag `hasNewerPages` so those evicted messages can
  //    be reloaded from the buffer head via newer-pagination on scroll-down.
  const trimActiveChannelBuffer = useCallback(
    (atBottom: boolean) => {
      const channelId = activeChannelIdRef.current;
      if (!channelId) return;
      setMessagesByChannel((prev) => {
        const st = prev[channelId];
        if (!st || st.messages.length <= MAX_SPACE_LOADED_MESSAGES) return prev;
        if (atBottom) {
          const trimmed = trimSpaceMessages(st.messages, 'newest');
          const newOldest = trimmed[trimmed.length - 1];
          return {
            ...prev,
            [channelId]: {
              ...st,
              messages: trimmed,
              olderCursor: newOldest ? newOldest.id : st.olderCursor,
              hasNewerPages: false,
            },
          };
        }
        const trimmed = trimSpaceMessages(st.messages, 'oldest');
        return {
          ...prev,
          [channelId]: {
            ...st,
            messages: trimmed,
            hasNewerPages: true,
          },
        };
      });
    },
    [setMessagesByChannel, activeChannelIdRef],
  );

  return {
    loadOlderMessages,
    loadNewerMessages,
    jumpToLatestMessages,
    fetchMessagesAround,
    trimActiveChannelBuffer,
  };
}
